/** @jsxImportSource theme-ui */
import { Heading, Box } from 'theme-ui';
import { useParams } from 'react-router-dom';
import { JSONTree } from 'react-json-tree';
import jsonTreeTheme from '@/theme/jsonTreeTheme';
import { useGetLicenseChangesLogQuery } from '@/store/apiService';
import DataTable from '@/components/DataTable';
import { format } from 'date-fns';
import { Container } from '@/components/styled';

const messageTypeLabels: Record<number, string> = {
    1: 'Subscription',
    2: 'Customer',
};

const messageTypeLabel = (messageType?: number) =>
    (messageType != null && messageTypeLabels[messageType]) || `Unknown (${messageType})`;

const parseMetaData = (value?: string) => {
    if (!value) return null;
    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
};

const columns = [
    { columnKey: 'Id', accessor: 'Id', Header: 'Id', width: '60px' },
    {
        columnKey: 'MessageType',
        accessor: 'MessageType',
        Header: 'Type',
        width: '110px',
        Cell: ({ row }) => <>{messageTypeLabel(row.original.MessageType)}</>,
    },
    {
        columnKey: 'MetaData',
        accessor: 'MetaData',
        Header: 'Metadata',
        Cell: ({ cell }) => {
            const metaData = parseMetaData(cell.value);
            if (metaData == null) return <>{cell.value ?? ''}</>;
            return (
                <Box
                    sx={{
                        '& > ul': {
                            padding: (t: any) => `${t.space[2]} !important`,
                            borderRadius: (t: any) => `${t.radii['sm']} !important`,
                        },
                    }}
                >
                    <JSONTree
                        data={metaData}
                        hideRoot={true}
                        theme={jsonTreeTheme}
                        invertTheme={true}
                        shouldExpandNodeInitially={(keyPath, data, level) => {
                            return false;
                        }}
                    />
                </Box>
            );
        },
    },
    {
        columnKey: 'VossSubscriptionId',
        accessor: 'VossSubscriptionId',
        Header: 'Voss subscription Id',
    },
    {
        columnKey: 'VossCustomerId',
        accessor: 'VossCustomerId',
        Header: 'Voss customer Id',
    },
    { columnKey: 'CompanyId', accessor: 'CompanyId', Header: 'Company Id' },
    { columnKey: 'CheckStatus', accessor: 'CheckStatus', Header: 'Status', width: '100px' },
    { columnKey: 'CheckNote', accessor: 'CheckNote', Header: 'Note', width: '25%' },
    {
        columnKey: 'CheckedAt',
        accessor: 'CheckedAt',
        Header: 'Checked',
        Cell: ({ row }) =>
            row.original.CheckedAt
                ? format(new Date(row.original.CheckedAt), 'd MMM yyyy, HH:mm:ss')
                : '',
    },
    {
        columnKey: 'CreatedDate',
        accessor: 'CreatedDate',
        Header: 'Created',
        Cell: ({ row }) => format(new Date(row.original.CreatedDate), 'd MMM yyyy, HH:mm:ss'),
    },
];

const LicenseChangesLog = () => {
    const { id } = useParams();
    const licenseChangesLog = useGetLicenseChangesLogQuery({});

    return (
        <Container>
            <Heading>License changes log</Heading>
            <DataTable
                data={licenseChangesLog?.data?.Results}
                columns={columns}
                isLoading={licenseChangesLog.isLoading}
                isFetching={licenseChangesLog.isFetching}
                sorting={true}
                filtering={true}
            />
        </Container>
    );
};

export default LicenseChangesLog;
